import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { useSpinDelay } from 'spin-delay'
import { cn, useIsPending } from '#app/utils/misc'

/** The full-width pill both the submit and the Google button share. */
export const AUTH_BUTTON_CLASS =
	'tt-auth-btn flex h-[50px] w-full items-center justify-center gap-2.5 rounded-full text-[16px] font-semibold transition-[transform,opacity,background-color] duration-200 ease-tt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2 min-[601px]:h-[clamp(48px,6.4vh,58px)] min-[601px]:text-[17px]'

/**
 * The primary action. Ink pill, hover opacity .92, active scale .99. While
 * pending it shows a spinner, sets aria-busy and refuses a second click.
 * `pending` falls back to the route's navigation (useIsPending); a fetcher
 * form passes `pending={fetcher.state !== 'idle'}` itself.
 */
export function AuthSubmit({
	children,
	pending,
	className,
	disabled,
	...rest
}: {
	children: ReactNode
	pending?: boolean
	form?: string
} & Omit<ComponentPropsWithoutRef<'button'>, 'type' | 'form'>) {
	const navigationPending = useIsPending()
	const isPending = pending ?? navigationPending
	const showSpinner = useSpinDelay(isPending, { delay: 200, minDuration: 400 })
	return (
		<button
			type="submit"
			aria-busy={isPending || undefined}
			disabled={disabled || isPending}
			className={cn(
				AUTH_BUTTON_CLASS,
				'mt-1 bg-tt-ink text-white hover:opacity-[.92] active:scale-[.99] disabled:cursor-wait disabled:opacity-80 min-[601px]:mt-[clamp(4px,1vh,10px)]',
				className,
			)}
			{...rest}
		>
			{showSpinner ? <Spinner /> : null}
			<span>{children}</span>
		</button>
	)
}

function Spinner() {
	return (
		<svg
			className="h-[18px] w-[18px] animate-spin"
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden="true"
		>
			<circle
				cx="12"
				cy="12"
				r="9"
				stroke="currentColor"
				strokeOpacity={0.3}
				strokeWidth={2.5}
			/>
			<path
				d="M21 12a9 9 0 0 0-9-9"
				stroke="currentColor"
				strokeWidth={2.5}
				strokeLinecap="round"
			/>
		</svg>
	)
}
